import { isDefined, isNotDefined } from '@sgme/fp';
import { TimeSeriesChart } from 'components/shared/Charts/TimeSeriesChart';
import { Percent } from 'components/shared/Percent/Percent';
import { SeriesLineOptions } from 'highcharts';
import { zip } from 'lodash-es';
import { FC, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { correlationsChanged } from 'state/quotes/correlations/correlations.action';
import { getCorrelationsByTabId } from 'state/quotes/correlations/correlations.selectors';
import { getQuoteUnderlyings } from 'state/selectors';
import { AppState } from 'state/state.model';

import { addConstant } from '../Graphs.helpers';
import { InputMap, TimeSeries, TimeSeriesAndTables } from '../Graphs.model';

interface CorrCarryBackTestGraphProps {
  tabIndex?: number;
  tabId: string;
  data: TimeSeriesAndTables;
}

export const CorrCarryBackTestGraph: FC<CorrCarryBackTestGraphProps> = props => {
  const { tabId, tabIndex, data } = props;
  const dispatch = useDispatch();
  const inputs = useSelector((state: AppState) => getCorrelationsByTabId(state, tabId));
  const nbUnderlyings = useSelector((state: AppState) => getQuoteUnderlyings(state, tabId))?.length;
  const seriesLineOptions = useGraphOptions(data, { ...inputs, nbUnderlyings });

  const handleInputChanged = useCallback(
    (correlCarry?: number) => dispatch(correlationsChanged(tabId, { ...inputs, correlCarry })),
    [tabId, dispatch, inputs],
  );

  return (
    <div className="d-flex flex-column gap-12px">
      <table className="table table-sm my-0, bg-lvl1 table-striped">
        <tbody>
          <tr>
            <th scope="row" className="align-middle">
              Correlation carry backtest
            </th>
            <td>
              <Percent
                tabIndex={tabIndex}
                className="form-control"
                id="correl-carry"
                value={inputs?.correlCarry}
                onChange={handleInputChanged}
                precision={4}
                e2e={'correl-carry-input'}
              />
            </td>
          </tr>
        </tbody>
      </table>
      <TimeSeriesChart seriesLineOptions={seriesLineOptions} />
    </div>
  );
};

function addTimeSeries(
  name: string,
  correlCarry: number,
  nbUnderlyings: number,
  timeSeries: TimeSeries,
): SeriesLineOptions[] {
  if (nbUnderlyings < 1) {
    return [];
  }

  const { columns, rows, index } = timeSeries;
  const constant = correlCarry / 100 - (1 / nbUnderlyings) * (correlCarry / 100 - 1);
  const bIdx = columns.findIndex(col => col.name === 'BasketVols');
  const aIdx = columns.findIndex(col => col.name === 'AvgIndivVols');
  const backTestSeries = rows.map(row => Math.sqrt(constant) * Number(row[aIdx]) - Number(row[bIdx]));

  return [
    {
      name,
      type: 'line',
      tooltip: { valueDecimals: 2 },
      data: zip(index, backTestSeries),
    },
  ];
}

function useGraphOptions(data: TimeSeriesAndTables, inputs: InputMap): SeriesLineOptions[] {
  return useMemo(() => {
    const correlCarry = inputs['correlCarry'] as number | undefined;
    const nbUnderlyings = inputs['nbUnderlyings'] as number | undefined;
    const timeSeries = data.timeSeries['CorrelCarryBacktest'];

    if (isNotDefined(nbUnderlyings) || isNotDefined(timeSeries)) {
      return [];
    }

    const zero = addConstant('Zero', 0, timeSeries.index);
    const series = isDefined(correlCarry)
      ? addTimeSeries('Correlation Carry Backtest', correlCarry, nbUnderlyings, timeSeries)
      : [];

    return [...zero, ...series];
  }, [data, inputs]);
}
